import { format } from "date-fns";

// Server returns timestamps as UTC `"YYYY-MM-DD HH:mm:ss"` with no zone
// marker — JS parses naive strings as local time. Coerce to UTC.
// See [[frontend-gotchas-quickvee]] §2.

const SERVER_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

export const parseServerTimestamp = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  if (typeof input !== "string") return new Date(input);
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(input)) return new Date(input);
  return new Date(input.replace(" ", "T") + "Z");
};

export const nowServerUtcTimestamp = () =>
  new Date().toISOString().slice(0, 19).replace("T", " ");

/** Normalize a server timestamp string; reject malformed values. */
export const coerceServerTimestamp = (value) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw || !SERVER_TIMESTAMP_RE.test(raw)) return null;
  const parsed = parseServerTimestamp(raw);
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  return raw.replace("T", " ").slice(0, 19);
};

const UPDATED_TIMESTAMP_PATHS = [
  (data) => data?.result?.updated_timestamp,
  (data) => data?.updated_timestamp,
  (data) => data?.data?.updated_timestamp,
];

/** Prefer a validated server timestamp from an API body; null when absent/invalid. */
export const extractUpdatedTimestamp = (data) => {
  for (const read of UPDATED_TIMESTAMP_PATHS) {
    const coerced = coerceServerTimestamp(read(data));
    if (coerced) return coerced;
  }
  return null;
};

/** Server timestamp from response when valid; otherwise current UTC in server format. */
export const resolveUpdatedTimestamp = (data) =>
  extractUpdatedTimestamp(data) ?? nowServerUtcTimestamp();

export const formatRelativeDate = (input) => {
  const t = parseServerTimestamp(input);
  if (!t || Number.isNaN(t.getTime())) return "";
  const diff = Math.max(0, Date.now() - t.getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const SERVER_DATETIME_PARTS_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;

function splitServerTimestampParts(input) {
  if (!input) return null;
  const match = String(input).trim().match(SERVER_DATETIME_PARTS_RE);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  return { year, month, day, hour, minute, second };
}

/** Stocktake list cell — MM/dd/yyyy from the server string as returned. */
export const formatStocktakeListDate = (input) => {
  const parts = splitServerTimestampParts(input);
  if (parts) return `${parts.month}/${parts.day}/${parts.year}`;
  const t = parseServerTimestamp(input);
  if (!t || Number.isNaN(t.getTime())) return "";
  return format(t, "MM/dd/yyyy");
};

/** Created tooltip — MM/dd/yyyy plus 24-hour clock time, unchanged from the API value. */
export const formatStocktakeDateTimeDetail = (input) => {
  const parts = splitServerTimestampParts(input);
  if (parts) {
    return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
  }
  const t = parseServerTimestamp(input);
  if (!t || Number.isNaN(t.getTime())) return "";
  return format(t, "MM/dd/yyyy HH:mm:ss");
};
